import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "../../components/Sidebar";
import { useAuth, API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  CreditCard, Plus, Eye, Loader2, ShieldCheck, Link2, Copy, FileSpreadsheet,
  BarChart3, Clock, CheckCircle2, XCircle, Search, KeyRound, Timer, ListChecks, Trash2, Mail, StickyNote, History, Pencil, Bot, Download, RefreshCw, Upload, Banknote
} from "lucide-react";

const fmtFecha = (s) => (s ? new Date(s).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" }) : "-");

const ESTADO_BADGE = {
  pendiente: "bg-amber-100 text-amber-800",
  aprobada: "bg-emerald-100 text-emerald-800",
  rechazada: "bg-red-100 text-red-700",
  expirada: "bg-slate-200 text-slate-600",
  eliminada: "bg-red-200 text-red-800",
  vista_admin: "bg-violet-100 text-violet-700",
  editada: "bg-indigo-100 text-indigo-700",
  enviada: "bg-blue-100 text-blue-700",
  otp_solicitado: "bg-amber-100 text-amber-800",
  vista_aseguradora: "bg-cyan-100 text-cyan-800",
  pago_iniciado: "bg-amber-100 text-amber-800",
  pago_esperando_aprobacion: "bg-amber-100 text-amber-800",
  pago_aprobado: "bg-blue-100 text-blue-700",
  pago_completado: "bg-emerald-100 text-emerald-800",
  pago_error: "bg-red-100 text-red-700",
  pago_cancelado: "bg-slate-200 text-slate-600",
};

const ESTADO_PAGO = {
  iniciado: "bg-blue-100 text-blue-700",
  en_curso: "bg-blue-100 text-blue-700",
  esperando_aprobacion: "bg-amber-100 text-amber-800",
  procesando: "bg-indigo-100 text-indigo-700",
  completado: "bg-emerald-100 text-emerald-800",
  error: "bg-red-100 text-red-700",
  cancelado: "bg-slate-200 text-slate-600",
  expirado: "bg-slate-200 text-slate-600",
};

const ESTADOS_PAGO_ACTIVOS = ["iniciado", "en_curso", "esperando_aprobacion", "procesando"];

export default function TarjetasCreditoPage() {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());

  const [tarjetas, setTarjetas] = useState([]);
  const [buscar, setBuscar] = useState("");
  const [loading, setLoading] = useState(true);
  const [aseguradoras, setAseguradoras] = useState([]);

  // Nueva tarjeta
  const [showNueva, setShowNueva] = useState(false);
  const [formT, setFormT] = useState({ cliente: "", titular: "", numero: "", vencimiento: "", cvv: "", notas: "", asignado_a: "" });
  const [asignables, setAsignables] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Solicitud de vista
  const [solTarjeta, setSolTarjeta] = useState(null); // tarjeta para la que se pide vista
  const [filtroAseg, setFiltroAseg] = useState("");
  const [solForm, setSolForm] = useState({ aseguradora: "", poliza: "" });
  const [enviandoSol, setEnviandoSol] = useState(false);
  const [solActiva, setSolActiva] = useState(null); // solicitud pendiente/aprobada en curso
  const [datosVista, setDatosVista] = useState(null); // datos descifrados
  const [segRestantes, setSegRestantes] = useState(0);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  // Aprobaciones (supervisor)
  const [porAprobar, setPorAprobar] = useState([]);
  const [aprobando, setAprobando] = useState(null); // solicitud seleccionada
  const [passSup, setPassSup] = useState("");
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [procesando, setProcesando] = useState(false);

  // Links
  const [links, setLinks] = useState([]);
  const [showLink, setShowLink] = useState(false);
  const [linkForm, setLinkForm] = useState({ cliente: "", nota: "" });
  const [creandoLink, setCreandoLink] = useState(false);
  const [emailLink, setEmailLink] = useState(null); // link al que se enviará email
  const [emailDest, setEmailDest] = useState("");
  const [enviandoEmail, setEnviandoEmail] = useState(false);

  // Eliminar tarjeta (admin)
  const [eliminarT, setEliminarT] = useState(null);
  const [motivoElim, setMotivoElim] = useState("");
  const [eliminando, setEliminando] = useState(false);

  // Ver notas
  const [verNotas, setVerNotas] = useState(null);

  // Compartir con aseguradora
  const [compartirT, setCompartirT] = useState(null);
  const [emailAseg, setEmailAseg] = useState("");
  const [compartiendo, setCompartiendo] = useState(false);

  // Enviar link de pago Seguros Crecer
  const [showCrecer, setShowCrecer] = useState(false);
  const [crecerT, setCrecerT] = useState(null);
  const [crecerForm, setCrecerForm] = useState({ nombre: "", telefono: "", email: "", canal: "whatsapp" });
  const [enviandoCrecer, setEnviandoCrecer] = useState(false);

  const abrirCrecer = (t = null) => {
    setCrecerT(t);
    setCrecerForm({ nombre: t?.cliente || "", telefono: "", email: "", canal: "whatsapp" });
    setShowCrecer(true);
  };

  const enviarCrecer = async () => {
    try {
      setEnviandoCrecer(true);
      const r = await axios.post(`${API}/cobros/seguros-crecer/enviar-link`, { ...crecerForm, tarjeta_id: crecerT?.id || null }, getAuthHeaders());
      const d = r.data;
      if (d.whatsapp === "enviado") toast.success("Link de pago enviado por WhatsApp");
      if (d.whatsapp === "fallido") toast.error("No se pudo enviar por WhatsApp (verifique el número)");
      if (d.email === "enviado") toast.success("Link de pago enviado por correo");
      if (d.email === "fallido") toast.error("No se pudo enviar el correo");
      setShowCrecer(false);
      fetchPagosHist();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar el link"); } finally { setEnviandoCrecer(false); }
  };

  // Editar tarjeta (admin/supervisor)
  const [editarT, setEditarT] = useState(null);
  const [formE, setFormE] = useState({ cliente: "", titular: "", numero: "", vencimiento: "", cvv: "", notas: "" });
  const [editando, setEditando] = useState(false);
  const [nuevoAsignado, setNuevoAsignado] = useState("");
  const [asignandoEj, setAsignandoEj] = useState(false);

  useEffect(() => {
    if ((showNueva || editarT) && asignables === null) {
      axios.get(`${API}/cobros/tarjetas-asignables`, getAuthHeaders()).then((r) => setAsignables(r.data || [])).catch(() => setAsignables([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNueva, editarT]);

  const agregarAsignado = async () => {
    if (!nuevoAsignado) return;
    try {
      setAsignandoEj(true);
      const res = await axios.post(`${API}/cobros/tarjetas/${editarT.id}/asignar`, { user_id: nuevoAsignado }, getAuthHeaders());
      setEditarT({ ...editarT, asignados: res.data.asignados });
      setNuevoAsignado("");
      toast.success("Ejecutivo asignado. Quedó en la trazabilidad.");
      fetchTarjetas(buscar);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al asignar"); } finally { setAsignandoEj(false); }
  };

  const quitarAsignado = async (uid) => {
    try {
      const res = await axios.delete(`${API}/cobros/tarjetas/${editarT.id}/asignar/${uid}`, getAuthHeaders());
      setEditarT({ ...editarT, asignados: res.data.asignados });
      toast.success("Acceso retirado. Quedó en la trazabilidad.");
      fetchTarjetas(buscar);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al quitar"); }
  };

  const abrirEditar = (t) => {
    setFormE({ cliente: t.cliente || "", titular: t.titular || "", numero: "", vencimiento: "", cvv: "", notas: t.notas || "" });
    setEditarT(t);
  };

  const guardarEdicion = async () => {
    try {
      setEditando(true);
      const res = await axios.put(`${API}/cobros/tarjetas/${editarT.id}`, formE, getAuthHeaders());
      const c = res.data.cambios || [];
      toast.success(c.length ? `Tarjeta actualizada (${c.join(", ")}). Quedó en la trazabilidad.` : "Sin cambios que guardar");
      setEditarT(null);
      fetchTarjetas(buscar);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al editar"); } finally { setEditando(false); }
  };

  // Pago La Colonial (robot WhatsApp)
  const [pagoT, setPagoT] = useState(null);
  const [pagoForm, setPagoForm] = useState({ cedula: "", poliza: "", monto: "" });
  const [pagoSesion, setPagoSesion] = useState(null);
  const [iniciandoPago, setIniciandoPago] = useState(false);
  const [passPago, setPassPago] = useState("");
  const [aprobandoPago, setAprobandoPago] = useState(false);
  const [emailRecibo, setEmailRecibo] = useState("");
  const [enviandoRecibo, setEnviandoRecibo] = useState(false);
  const pagoPollRef = useRef(null);

  const refrescarPago = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/cobros/pagos-colonial/${id}`, getAuthHeaders());
      setPagoSesion(r.data);
      if (!ESTADOS_PAGO_ACTIVOS.includes(r.data.estado) && pagoPollRef.current) {
        clearInterval(pagoPollRef.current); pagoPollRef.current = null;
      }
    } catch { /* sigue */ }
  }, [getAuthHeaders]);

  const iniciarPollPago = useCallback((id) => {
    if (pagoPollRef.current) clearInterval(pagoPollRef.current);
    pagoPollRef.current = setInterval(() => refrescarPago(id), 3500);
  }, [refrescarPago]);

  const abrirPago = async (t) => {
    setPagoT(t); setPagoSesion(null); setPagoForm({ cedula: "", poliza: "", monto: "" }); setPassPago(""); setEmailRecibo("");
    try {
      const r = await axios.get(`${API}/cobros/pagos-colonial?tarjeta_id=${t.id}`, getAuthHeaders());
      const ses = (r.data || [])[0];
      if (ses && (ESTADOS_PAGO_ACTIVOS.includes(ses.estado) || ses.estado === "completado")) {
        setPagoSesion(ses);
        if (ESTADOS_PAGO_ACTIVOS.includes(ses.estado)) iniciarPollPago(ses.id);
      }
    } catch { /* sin sesiones previas */ }
  };

  const cerrarPago = () => {
    if (pagoPollRef.current) { clearInterval(pagoPollRef.current); pagoPollRef.current = null; }
    setPagoT(null); setPagoSesion(null); setPassPago("");
  };

  const iniciarPago = async () => {
    try {
      setIniciandoPago(true);
      const r = await axios.post(`${API}/cobros/tarjetas/${pagoT.id}/pagar-colonial`, pagoForm, getAuthHeaders());
      setPagoSesion(r.data.sesion);
      toast.success("Robot iniciado: conversando con el bot de La Colonial...");
      iniciarPollPago(r.data.sesion.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al iniciar el pago"); } finally { setIniciandoPago(false); }
  };

  const aprobarPago = async () => {
    try {
      setAprobandoPago(true);
      await axios.post(`${API}/cobros/pagos-colonial/${pagoSesion.id}/aprobar`, { password: passPago }, getAuthHeaders());
      toast.success("Pago confirmado al bot de La Colonial");
      setPassPago("");
      refrescarPago(pagoSesion.id); iniciarPollPago(pagoSesion.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al aprobar"); } finally { setAprobandoPago(false); }
  };

  const cancelarPago = async () => {
    try {
      await axios.post(`${API}/cobros/pagos-colonial/${pagoSesion.id}/cancelar`, {}, getAuthHeaders());
      toast.success("Pago cancelado");
      refrescarPago(pagoSesion.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cancelar"); }
  };

  const descargarReciboSesion = async (s) => {
    try {
      const res = await axios.get(`${API}/cobros/pagos-colonial/${s.id}/recibo`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = s.recibo?.filename || "recibo_colonial"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar el recibo"); }
  };
  const descargarRecibo = () => descargarReciboSesion(pagoSesion);

  const [eliminarLink, setEliminarLink] = useState(null);
  const [eliminandoLink, setEliminandoLink] = useState(false);

  // Importar / Exportar masivo (solo admin)
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importando, setImportando] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const descargarBlob = async (url, nombre) => {
    const res = await axios.get(url, { ...getAuthHeaders(), responseType: "blob" });
    const u = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = u; a.download = nombre; a.click();
    URL.revokeObjectURL(u);
  };

  const descargarPlantilla = async () => {
    try { await descargarBlob(`${API}/cobros/tarjetas-plantilla`, "plantilla_importacion_tarjetas.xlsx"); }
    catch { toast.error("Error al descargar la plantilla"); }
  };

  const exportarTarjetas = async () => {
    try {
      await descargarBlob(`${API}/cobros/tarjetas-exportar`, `tarjetas_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Exportación descargada. Maneje el archivo con cuidado: contiene datos completos de tarjetas.");
    } catch { toast.error("Error al exportar"); }
  };

  const importarTarjetas = async () => {
    if (!importFile) return;
    try {
      setImportando(true);
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await axios.post(`${API}/cobros/tarjetas-importar`, fd, getAuthHeaders());
      setImportResult(r.data);
      if (r.data.creadas > 0) { toast.success(`${r.data.creadas} tarjeta(s) importada(s)`); fetchTarjetas(); }
      else if ((r.data.errores || []).length === 0) toast.info("El archivo no tenía filas para importar");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al importar"); } finally { setImportando(false); }
  };

  const confirmarEliminarLink = async () => {
    try {
      setEliminandoLink(true);
      await axios.delete(`${API}/cobros/links/${eliminarLink.id}`, getAuthHeaders());
      toast.success("Link eliminado");
      setEliminarLink(null);
      fetchLinks();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar el link"); } finally { setEliminandoLink(false); }
  };

  const descargarPagosExcel = async () => {
    try {
      const params = new URLSearchParams();
      params.set("filtro", filtroPagos);
      if (pagosDesde) params.set("desde", pagosDesde);
      if (pagosHasta) params.set("hasta", pagosHasta);
      const res = await axios.get(`${API}/cobros/pagos-unificados/excel?${params.toString()}`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pagos_${filtroPagos}_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al exportar el Excel"); }
  };

  const enviarRecibo = async () => {
    try {
      setEnviandoRecibo(true);
      await axios.post(`${API}/cobros/pagos-colonial/${pagoSesion.id}/recibo/email`, { email: emailRecibo }, getAuthHeaders());
      toast.success(`Recibo enviado a ${emailRecibo}`);
      setEmailRecibo("");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar el recibo"); } finally { setEnviandoRecibo(false); }
  };

  const compartirAseguradora = async () => {
    try {
      setCompartiendo(true);
      await axios.post(`${API}/cobros/tarjetas/${compartirT.id}/compartir`,
        { email: emailAseg, base_url: window.location.origin }, getAuthHeaders());
      toast.success(`Enviado a ${emailAseg}. Recibirá el OTP cuando la aseguradora lo solicite.`);
      setCompartirT(null); setEmailAseg("");
      fetchMetricas();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); } finally { setCompartiendo(false); }
  };

  // Historial / trazabilidad por tarjeta
  const [historial, setHistorial] = useState(null);

  const abrirHistorial = async (t) => {
    try {
      const res = await axios.get(`${API}/cobros/tarjetas/${t.id}/historial`, getAuthHeaders());
      setHistorial(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cargar historial"); }
  };

  const exportarHistorialPdf = async () => {
    try {
      const res = await axios.get(`${API}/cobros/tarjetas/${historial.tarjeta.id}/historial/pdf`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `trazabilidad_tarjeta_${historial.tarjeta.ultimos4}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al exportar el PDF"); }
  };

  // Reporte / métricas
  const [reporte, setReporte] = useState([]);
  const [repDesde, setRepDesde] = useState("");
  const [repHasta, setRepHasta] = useState("");
  const [metricas, setMetricas] = useState(null);
  const [alcance, setAlcance] = useState({ es_admin: false, es_supervisor: false });

  const fetchTarjetas = useCallback(async (q = "") => {
    try {
      const res = await axios.get(`${API}/cobros/tarjetas${q ? `?buscar=${encodeURIComponent(q)}` : ""}`, getAuthHeaders());
      setTarjetas(res.data || []);
    } catch { toast.error("Error cargando tarjetas"); } finally { setLoading(false); }
  }, [getAuthHeaders]);

  const fetchPorAprobar = useCallback(async () => {
    try { const res = await axios.get(`${API}/cobros/solicitudes/por-aprobar`, getAuthHeaders()); setPorAprobar(res.data || []); } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  const fetchLinks = useCallback(async () => {
    try { const res = await axios.get(`${API}/cobros/links`, getAuthHeaders()); setLinks(res.data || []); } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  const fetchReporte = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (repDesde) params.set("desde", repDesde);
      if (repHasta) params.set("hasta", repHasta);
      const res = await axios.get(`${API}/cobros/reporte?${params.toString()}`, getAuthHeaders());
      setReporte(res.data || []);
    } catch { toast.error("Error cargando reporte"); }
  }, [getAuthHeaders, repDesde, repHasta]);

  const fetchMetricas = useCallback(async () => {
    try { const res = await axios.get(`${API}/cobros/metricas`, getAuthHeaders()); setMetricas(res.data); } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  const [pagosHist, setPagosHist] = useState([]);
  const [crecerEnvios, setCrecerEnvios] = useState([]);
  const [filtroPagos, setFiltroPagos] = useState("todos");
  const [pagosDesde, setPagosDesde] = useState("");
  const [pagosHasta, setPagosHasta] = useState("");
  const fetchPagosHist = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (pagosDesde) params.set("desde", pagosDesde);
      if (pagosHasta) params.set("hasta", pagosHasta);
      const res = await axios.get(`${API}/cobros/pagos-colonial?${params.toString()}`, getAuthHeaders());
      setPagosHist(res.data || []);
      const rc = await axios.get(`${API}/cobros/seguros-crecer/envios?${params.toString()}`, getAuthHeaders());
      setCrecerEnvios(rc.data || []);
    } catch { /* silencioso */ }
  }, [getAuthHeaders, pagosDesde, pagosHasta]);

  useEffect(() => {
    fetchTarjetas();
    fetchPorAprobar();
    fetchLinks();
    fetchMetricas();
    fetchPagosHist();
    axios.get(`${API}/cobros/aseguradoras`, getAuthHeaders()).then((r) => setAseguradoras(r.data || [])).catch(() => {});
    axios.get(`${API}/cobros/mi-alcance`, getAuthHeaders()).then((r) => setAlcance(r.data || {})).catch(() => {});
    const t = setInterval(fetchPorAprobar, 20000);
    return () => { clearInterval(t); if (pollRef.current) clearInterval(pollRef.current); if (timerRef.current) clearInterval(timerRef.current); if (pagoPollRef.current) clearInterval(pagoPollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guardarTarjeta = async () => {
    try {
      setGuardando(true);
      await axios.post(`${API}/cobros/tarjetas`, { ...formT, asignado_a: formT.asignado_a || null }, getAuthHeaders());
      toast.success("Tarjeta guardada de forma cifrada");
      setShowNueva(false);
      setFormT({ cliente: "", titular: "", numero: "", vencimiento: "", cvv: "", notas: "", asignado_a: "" });
      fetchTarjetas(buscar);
      fetchMetricas();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); } finally { setGuardando(false); }
  };

  const cerrarVista = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    setSolActiva(null); setDatosVista(null); setSolTarjeta(null); setSegRestantes(0);
    setSolForm({ aseguradora: "", poliza: "" }); setFiltroAseg("");
  }, []);

  const iniciarCountdown = useCallback((expiraIso, solicitudId) => {
    const tick = async () => {
      const restante = Math.max(0, Math.round((new Date(expiraIso) - new Date()) / 1000));
      setSegRestantes(restante);
      if (restante <= 0) {
        toast.info("La vista se bloqueó (5 minutos). Debe solicitar autorización nuevamente.");
        cerrarVista();
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  }, [cerrarVista]);

  const iniciarPolling = useCallback((sol) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/cobros/solicitudes/mias`, getAuthHeaders());
        const actual = (r.data || []).find((x) => x.id === sol.id);
        if (!actual) return;
        if (actual.estado === "aprobada") {
          clearInterval(pollRef.current);
          const v = await axios.get(`${API}/cobros/solicitudes/${sol.id}/ver`, getAuthHeaders());
          setDatosVista(v.data);
          setSolActiva(actual);
          iniciarCountdown(v.data.expira_at, sol.id);
        } else if (actual.estado === "rechazada") {
          clearInterval(pollRef.current);
          toast.error(`Solicitud rechazada${actual.motivo_rechazo ? `: ${actual.motivo_rechazo}` : ""}`);
          cerrarVista();
        }
      } catch { /* sigue esperando */ }
    }, 4000);
  }, [cerrarVista, iniciarCountdown]);

  const solicitarVista = async () => {
    try {
      setEnviandoSol(true);
      const res = await axios.post(`${API}/cobros/tarjetas/${solTarjeta.id}/solicitar-vista`, solForm, getAuthHeaders());
      const sol = res.data.solicitud;
      setSolActiva(sol);
      toast.success("Solicitud enviada a su supervisor. Esperando aprobación...");
      iniciarPolling(sol);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al solicitar"); setEnviandoSol(false); return; }
    setEnviandoSol(false);
  };

  const abrirVerDatos = async (t) => {
    if (isAdmin) {
      try {
        const res = await axios.get(`${API}/cobros/tarjetas/${t.id}/ver-admin`, getAuthHeaders());
        setSolTarjeta(t);
        setDatosVista(res.data);
      } catch (e) { toast.error(e.response?.data?.detail || "Error al ver la tarjeta"); }
    } else {
      setSolTarjeta(t);
      try {
        const r = await axios.get(`${API}/cobros/solicitudes/mias`, getAuthHeaders());
        const sols = (r.data || []).filter((x) => x.tarjeta_id === t.id);
        const aprobada = sols.find((x) => x.estado === "aprobada" && x.expira_at && new Date(x.expira_at) > new Date());
        if (aprobada) {
          const v = await axios.get(`${API}/cobros/solicitudes/${aprobada.id}/ver`, getAuthHeaders());
          setDatosVista(v.data);
          setSolActiva(aprobada);
          iniciarCountdown(v.data.expira_at, aprobada.id);
          toast.success("Autorización vigente: no necesita solicitar nuevamente");
          return;
        }
        const pendiente = sols.find((x) => x.estado === "pendiente");
        if (pendiente) {
          setSolActiva(pendiente);
          iniciarPolling(pendiente);
        }
      } catch { /* abre el formulario normal */ }
    }
  };

  const aprobar = async () => {
    try {
      setProcesando(true);
      await axios.post(`${API}/cobros/solicitudes/${aprobando.id}/aprobar`, { password: passSup }, getAuthHeaders());
      toast.success("Autorización aprobada. El ejecutivo tiene 5 minutos para ver los datos.");
      setAprobando(null); setPassSup("");
      fetchPorAprobar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al aprobar"); } finally { setProcesando(false); }
  };

  const rechazar = async () => {
    try {
      setProcesando(true);
      await axios.post(`${API}/cobros/solicitudes/${aprobando.id}/rechazar`, { motivo: motivoRechazo }, getAuthHeaders());
      toast.success("Solicitud rechazada");
      setAprobando(null); setPassSup(""); setMotivoRechazo("");
      fetchPorAprobar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al rechazar"); } finally { setProcesando(false); }
  };

  const crearLink = async () => {
    try {
      setCreandoLink(true);
      const res = await axios.post(`${API}/cobros/links`, linkForm, getAuthHeaders());
      const url = `${window.location.origin}/registro-tarjeta/${res.data.link.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Link generado y copiado al portapapeles");
      setShowLink(false); setLinkForm({ cliente: "", nota: "" });
      fetchLinks(); fetchMetricas();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al generar link"); } finally { setCreandoLink(false); }
  };

  const copiarLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/registro-tarjeta/${token}`)
      .then(() => toast.success("Link copiado")).catch(() => toast.error("No se pudo copiar"));
  };

  const enviarEmail = async () => {
    try {
      setEnviandoEmail(true);
      await axios.post(`${API}/cobros/links/${emailLink.id}/enviar-email`,
        { email: emailDest, base_url: window.location.origin }, getAuthHeaders());
      toast.success(`Link enviado por correo a ${emailDest}`);
      setEmailLink(null); setEmailDest("");
      fetchLinks();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar el correo"); } finally { setEnviandoEmail(false); }
  };

  const eliminarTarjeta = async () => {
    try {
      setEliminando(true);
      await axios.post(`${API}/cobros/tarjetas/${eliminarT.id}/eliminar`, { motivo: motivoElim }, getAuthHeaders());
      toast.success("Tarjeta eliminada. Quedó registrada en el reporte.");
      setEliminarT(null); setMotivoElim("");
      fetchTarjetas(buscar); fetchMetricas();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); } finally { setEliminando(false); }
  };

  const descargarExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (repDesde) params.set("desde", repDesde);
      if (repHasta) params.set("hasta", repHasta);
      const res = await axios.get(`${API}/cobros/reporte/excel?${params.toString()}`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "reporte_tarjetas.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar Excel"); }
  };

  const asegFiltradas = aseguradoras.filter((a) => a.toLowerCase().includes(filtroAseg.toLowerCase()));

  const pagosUnificados = [
    ...(filtroPagos !== "crecer" ? pagosHist.map((s) => ({ tipo: "colonial", fecha: s.iniciado_at, s })) : []),
    ...(filtroPagos !== "colonial" ? crecerEnvios.map((e) => ({ tipo: "crecer", fecha: e.solicitada_at, s: e })) : []),
  ].sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="cobros-tarjetas-page">
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-[#0C314C] via-[#123f61] to-[#1a5a8a] p-6 shadow-lg" data-testid="cobros-header">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><CreditCard className="w-6 h-6 text-white" /></span>
            Tarjetas de Crédito
          </h1>
          <p className="text-blue-100/90 mt-1.5">Registro cifrado de tarjetas, vista autorizada por supervisor y trazabilidad completa</p>
        </div>

        <Tabs defaultValue="tarjetas" onValueChange={(v) => { if (v === "reporte") fetchReporte(); if (v === "metricas") fetchMetricas(); if (v === "aprobaciones") fetchPorAprobar(); }}>
          <TabsList data-testid="cobros-tabs" className="flex-wrap h-auto">
            <TabsTrigger value="tarjetas" data-testid="tab-tarjetas"><CreditCard className="w-4 h-4 mr-1" />Tarjetas</TabsTrigger>
            <TabsTrigger value="aprobaciones" data-testid="tab-aprobaciones">
              <ShieldCheck className="w-4 h-4 mr-1" />Aprobaciones
              {porAprobar.length > 0 && <Badge className="ml-1.5 bg-amber-500 text-white text-xs px-1.5">{porAprobar.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="links" data-testid="tab-links"><Link2 className="w-4 h-4 mr-1" />Links Clientes</TabsTrigger>
            <TabsTrigger value="reporte" data-testid="tab-reporte"><ListChecks className="w-4 h-4 mr-1" />Reporte</TabsTrigger>
            <TabsTrigger value="pagos" data-testid="tab-pagos"><Bot className="w-4 h-4 mr-1" />Pagos</TabsTrigger>
            <TabsTrigger value="metricas" data-testid="tab-metricas"><BarChart3 className="w-4 h-4 mr-1" />Métricas</TabsTrigger>
          </TabsList>

          {/* ===== TARJETAS ===== */}
          <TabsContent value="tarjetas" className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2 justify-between">
              <div className="relative w-72">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input className="pl-8" placeholder="Buscar cliente, titular o últimos 4..." value={buscar} data-testid="buscar-tarjetas"
                  onChange={(e) => { setBuscar(e.target.value); fetchTarjetas(e.target.value); }} />
              </div>
              <div className="flex gap-2">
                {isAdmin && (
                  <>
                    <Button variant="outline" onClick={() => { setShowImport(true); setImportResult(null); setImportFile(null); }} data-testid="importar-btn">
                      <Upload className="w-4 h-4 mr-1" />Importar
                    </Button>
                    <Button variant="outline" onClick={exportarTarjetas} data-testid="exportar-btn">
                      <Download className="w-4 h-4 mr-1" />Exportar
                    </Button>
                  </>
                )}
                <Button variant="outline" className="text-teal-700 border-teal-300 hover:bg-teal-50" onClick={() => abrirCrecer(null)} data-testid="crecer-link-btn">
                  <Banknote className="w-4 h-4 mr-1" />Pago Seguros Crecer
                </Button>
                {(isAdmin || alcance.es_supervisor) && (
                  <Button onClick={() => setShowNueva(true)} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="nueva-tarjeta-btn">
                    <Plus className="w-4 h-4 mr-1" />Nueva Tarjeta
                  </Button>
                )}
              </div>
            </div>
            {tarjetas.some((t) => t.vencimiento_estado) && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2" data-testid="alerta-vencimientos">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>
                  {tarjetas.filter((t) => t.vencimiento_estado === "vencida").length > 0 && <strong>{tarjetas.filter((t) => t.vencimiento_estado === "vencida").length} tarjeta(s) vencida(s)</strong>}
                  {tarjetas.filter((t) => t.vencimiento_estado === "vencida").length > 0 && tarjetas.filter((t) => t.vencimiento_estado === "por_vencer").length > 0 && " y "}
                  {tarjetas.filter((t) => t.vencimiento_estado === "por_vencer").length > 0 && <strong>{tarjetas.filter((t) => t.vencimiento_estado === "por_vencer").length} tarjeta(s) próxima(s) a vencer</strong>}
                  {" — solicite al cliente la tarjeta actualizada."}
                </span>
              </div>
            )}
            {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div> : (
              <Card className="shadow-md border-slate-200 dark:border-slate-700 overflow-hidden"><CardContent className="p-0 overflow-x-auto max-h-[65vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white">
                    <th className="p-3 font-semibold whitespace-nowrap">Cliente</th><th className="p-3 font-semibold whitespace-nowrap">Titular</th><th className="p-3 font-semibold whitespace-nowrap">Tarjeta</th>
                    <th className="p-3 font-semibold whitespace-nowrap">Origen</th><th className="p-3 font-semibold whitespace-nowrap">Registrada por</th><th className="p-3 font-semibold whitespace-nowrap">Fecha</th><th className="p-3 font-semibold whitespace-nowrap">Acción</th>
                  </tr></thead>
                  <tbody>
                    {tarjetas.length === 0 ? (
                      <tr><td colSpan={7} className="p-8 text-center text-slate-400">No hay tarjetas registradas</td></tr>
                    ) : tarjetas.map((t, i) => (
                      <tr key={t.id} className={`border-b hover:bg-blue-50/60 dark:hover:bg-slate-800/60 ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`} data-testid={`tarjeta-row-${t.id}`}>
                        <td className="p-2.5 font-medium dark:text-white">{t.cliente}</td>
                        <td className="p-2.5">{t.titular}</td>
                        <td className="p-2.5 font-mono whitespace-nowrap">•••• {t.ultimos4} <Badge variant="outline" className="ml-1 text-xs">{t.marca}</Badge>
                          {t.vencimiento_estado === "vencida" && <Badge className="ml-1 text-xs bg-red-100 text-red-700 font-sans" data-testid={`venc-badge-${t.id}`}>Vencida</Badge>}
                          {t.vencimiento_estado === "por_vencer" && <Badge className="ml-1 text-xs bg-amber-100 text-amber-800 font-sans" data-testid={`venc-badge-${t.id}`}>Vence pronto</Badge>}
                        </td>
                        <td className="p-2.5">{t.origen === "cliente" ? <Badge className="bg-blue-100 text-blue-700">Cliente</Badge> : <Badge variant="outline">Interno</Badge>}</td>
                        <td className="p-2.5 text-slate-500">{t.created_by_name}{(t.asignados?.length > 0) && <span className="block text-xs text-emerald-700 dark:text-emerald-400" data-testid={`asignada-a-${t.id}`}>Asignada a {t.asignados.map((a) => a.nombre).join(", ")}</span>}</td>
                        <td className="p-2.5 text-slate-500 whitespace-nowrap">{fmtFecha(t.created_at)}</td>
                        <td className="p-2.5">
                          <div className="flex gap-1 flex-wrap">
                            <Button size="sm" variant="outline" className="h-8 px-2" title="Ver datos" onClick={() => abrirVerDatos(t)} data-testid={`ver-datos-btn-${t.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 px-2 text-slate-600" title="Notas" onClick={() => setVerNotas(t)} data-testid={`ver-notas-btn-${t.id}`}>
                              <StickyNote className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 px-2 text-[#0C314C]" title="Enviar a aseguradora" onClick={() => { setCompartirT(t); setEmailAseg(""); }} data-testid={`compartir-btn-${t.id}`}>
                              <Mail className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 px-2 text-emerald-700" title="Pagar en La Colonial" onClick={() => abrirPago(t)} data-testid={`pagar-colonial-btn-${t.id}`}>
                              <Bot className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 px-2 text-teal-700" title="Enviar link de pago Seguros Crecer" onClick={() => abrirCrecer(t)} data-testid={`crecer-btn-${t.id}`}>
                              <Banknote className="w-4 h-4" />
                            </Button>
                            {(alcance.es_admin || alcance.es_supervisor) && (
                              <Button size="sm" variant="outline" className="h-8 px-2 text-indigo-600" title="Editar" onClick={() => abrirEditar(t)} data-testid={`editar-tarjeta-btn-${t.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {(alcance.es_admin || alcance.es_supervisor) && (
                              <Button size="sm" variant="outline" className="h-8 px-2 text-slate-600" title="Historial" onClick={() => abrirHistorial(t)} data-testid={`historial-btn-${t.id}`}>
                                <History className="w-4 h-4" />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="outline" className="h-8 px-2 text-red-600 border-red-200 hover:bg-red-50" title="Eliminar" onClick={() => setEliminarT(t)} data-testid={`eliminar-tarjeta-btn-${t.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>
            )}
          </TabsContent>

          {/* ===== APROBACIONES ===== */}
          <TabsContent value="aprobaciones" className="mt-4">
            {porAprobar.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-400">
                <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />No tiene solicitudes pendientes de aprobar
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {porAprobar.map((s) => (
                  <Card key={s.id} data-testid={`solicitud-aprobar-${s.id}`}><CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm space-y-0.5">
                      <p className="font-medium dark:text-white">{s.solicitante_nombre} solicita ver la tarjeta •••• {s.ultimos4} de <strong>{s.cliente}</strong></p>
                      <p className="text-slate-500">Para pagar: <strong>{s.aseguradora}</strong> · Póliza <strong>{s.poliza}</strong></p>
                      <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{fmtFecha(s.solicitada_at)}</p>
                    </div>
                    <Button onClick={() => setAprobando(s)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`revisar-solicitud-${s.id}`}>
                      <KeyRound className="w-4 h-4 mr-1" />Revisar
                    </Button>
                  </CardContent></Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== LINKS ===== */}
          <TabsContent value="links" className="mt-4 space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowLink(true)} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="generar-link-btn">
                <Link2 className="w-4 h-4 mr-1" />Generar Link para Cliente
              </Button>
            </div>
            <Card className="shadow-md border-slate-200 dark:border-slate-700 overflow-hidden"><CardContent className="p-0 overflow-x-auto max-h-[65vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white">
                  <th className="p-3 font-semibold whitespace-nowrap">Cliente</th><th className="p-3 font-semibold whitespace-nowrap">Estado</th><th className="p-3 font-semibold whitespace-nowrap">Generado por</th>
                  <th className="p-3 font-semibold whitespace-nowrap">Fecha</th><th className="p-3 font-semibold whitespace-nowrap">Completado</th><th className="p-3 font-semibold whitespace-nowrap">Acción</th>
                </tr></thead>
                <tbody>
                  {links.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-slate-400">No ha generado links todavía</td></tr>
                  ) : links.map((l, i) => (
                    <tr key={l.id} className={`border-b hover:bg-blue-50/60 dark:hover:bg-slate-800/60 ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`} data-testid={`link-row-${l.id}`}>
                      <td className="p-3 font-medium dark:text-white">{l.cliente}</td>
                      <td className="p-3">{l.estado === "completado"
                        ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Completado</Badge>
                        : <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>}</td>
                      <td className="p-3 text-slate-500">{l.created_by_name}</td>
                      <td className="p-3 text-slate-500">{fmtFecha(l.created_at)}</td>
                      <td className="p-3 text-slate-500">{fmtFecha(l.completado_at)}</td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1.5">
                            {l.estado === "pendiente" && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => copiarLink(l.token)} data-testid={`copiar-link-${l.id}`}>
                                  <Copy className="w-4 h-4 mr-1" />Copiar
                                </Button>
                                <Button size="sm" variant="outline" className="text-[#0C314C]" onClick={() => { setEmailLink(l); setEmailDest(l.ultimo_email_enviado || ""); }} data-testid={`email-link-${l.id}`}>
                                  <Mail className="w-4 h-4 mr-1" />Enviar por correo
                                </Button>
                              </>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="outline" className="h-8 px-2 text-red-600 border-red-200 hover:bg-red-50" title="Eliminar link" onClick={() => setEliminarLink(l)} data-testid={`eliminar-link-${l.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          {l.estado === "pendiente" && l.ultimo_email_enviado && (
                            <p className="text-xs text-slate-400">Enviado a {l.ultimo_email_enviado} · {fmtFecha(l.ultimo_email_at)}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* ===== REPORTE ===== */}
          <TabsContent value="reporte" className="mt-4 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div><Label className="text-xs">Desde</Label><Input type="date" value={repDesde} onChange={(e) => setRepDesde(e.target.value)} data-testid="reporte-desde" /></div>
              <div><Label className="text-xs">Hasta</Label><Input type="date" value={repHasta} onChange={(e) => setRepHasta(e.target.value)} data-testid="reporte-hasta" /></div>
              <Button variant="outline" onClick={fetchReporte} data-testid="reporte-filtrar-btn"><Search className="w-4 h-4 mr-1" />Filtrar</Button>
              <Button onClick={descargarExcel} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reporte-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1" />Excel
              </Button>
            </div>
            <Card className="shadow-md border-slate-200 dark:border-slate-700 overflow-hidden"><CardContent className="p-0 overflow-x-auto max-h-[65vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white">
                  <th className="p-3 font-semibold whitespace-nowrap">Fecha/Hora</th><th className="p-3 font-semibold whitespace-nowrap">Ejecutivo</th><th className="p-3 font-semibold whitespace-nowrap">Cliente</th><th className="p-3 font-semibold whitespace-nowrap">Tarjeta</th>
                  <th className="p-3 font-semibold whitespace-nowrap">Aseguradora</th><th className="p-3 font-semibold whitespace-nowrap">Póliza</th><th className="p-3 font-semibold whitespace-nowrap">Supervisor</th><th className="p-3 font-semibold whitespace-nowrap">Estado</th><th className="p-3 font-semibold whitespace-nowrap">Vista</th>
                </tr></thead>
                <tbody data-testid="reporte-body">
                  {reporte.length === 0 ? (
                    <tr><td colSpan={9} className="p-8 text-center text-slate-400">Sin solicitudes en el período</td></tr>
                  ) : reporte.map((r, i) => (
                    <tr key={r.id} className={`border-b hover:bg-blue-50/60 dark:hover:bg-slate-800/60 ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`}>
                      <td className="p-3 text-slate-500 whitespace-nowrap">{fmtFecha(r.solicitada_at)}</td>
                      <td className="p-3">{r.solicitante_nombre}</td>
                      <td className="p-3 font-medium dark:text-white">{r.cliente}</td>
                      <td className="p-3 font-mono">•••• {r.ultimos4}</td>
                      <td className="p-3">{r.aseguradora}</td>
                      <td className="p-3">{r.poliza}</td>
                      <td className="p-3 text-slate-500">{r.supervisor_nombre}</td>
                      <td className="p-3"><Badge className={ESTADO_BADGE[r.estado] || ""}>{r.estado}</Badge></td>
                      <td className="p-3 text-slate-500 whitespace-nowrap">{fmtFecha(r.visto_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* ===== MÉTRICAS ===== */}
          {/* ===== TAB PAGOS (Colonial + Seguros Crecer) ===== */}
          <TabsContent value="pagos" className="mt-4 space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-xs">Aseguradora</Label>
                  <div className="flex gap-1.5 mt-1">
                    {[["todos", "Todas"], ["colonial", "La Colonial"], ["crecer", "Seguros Crecer"]].map(([v, l]) => (
                      <Button key={v} type="button" size="sm" variant={filtroPagos === v ? "default" : "outline"}
                        className={filtroPagos === v ? "bg-[#0C314C] hover:bg-[#0a2940] text-white" : ""}
                        onClick={() => setFiltroPagos(v)} data-testid={`pagos-filtro-${v}`}>{l}</Button>
                    ))}
                  </div>
                </div>
                <div><Label className="text-xs">Desde</Label><Input type="date" value={pagosDesde} onChange={(e) => setPagosDesde(e.target.value)} data-testid="pagos-desde" /></div>
                <div><Label className="text-xs">Hasta</Label><Input type="date" value={pagosHasta} onChange={(e) => setPagosHasta(e.target.value)} data-testid="pagos-hasta" /></div>
                <Button variant="outline" onClick={fetchPagosHist} data-testid="pagos-filtrar-btn"><Search className="w-4 h-4 mr-1" />Filtrar</Button>
                <Button onClick={descargarPagosExcel} className="bg-emerald-600 hover:bg-emerald-700 text-white" title="Exporta según el filtro de aseguradora activo" data-testid="pagos-excel-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-1" />Excel
                </Button>
              </div>
              <p className="text-sm text-slate-500">Pagos con el robot en La Colonial y links de pago Seguros Crecer enviados (según su jerarquía)</p>
            </div>
            <Card className="shadow-md border-slate-200 dark:border-slate-700 overflow-hidden"><CardContent className="p-0 overflow-x-auto max-h-[65vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white">
                  <th className="p-3 font-semibold whitespace-nowrap">Fecha/Hora</th><th className="p-3 font-semibold whitespace-nowrap">Aseguradora</th><th className="p-3 font-semibold whitespace-nowrap">Cliente</th>
                  <th className="p-3 font-semibold whitespace-nowrap">Tarjeta</th><th className="p-3 font-semibold whitespace-nowrap">Detalle</th><th className="p-3 font-semibold whitespace-nowrap">Ejecutivo</th>
                  <th className="p-3 font-semibold whitespace-nowrap">Aprobado por</th><th className="p-3 font-semibold whitespace-nowrap">Estado</th><th className="p-3 font-semibold whitespace-nowrap">Recibo</th>
                </tr></thead>
                <tbody data-testid="pagos-body">
                  {pagosUnificados.length === 0 ? (
                    <tr><td colSpan={9} className="p-8 text-center text-slate-400">Aún no hay pagos ni links de pago enviados</td></tr>
                  ) : pagosUnificados.map(({ tipo, s }, i) => (
                    <tr key={`${tipo}-${s.id}`} className={`border-b hover:bg-blue-50/60 dark:hover:bg-slate-800/60 ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`} data-testid={`pago-row-${tipo}-${s.id}`}>
                      <td className="p-3 text-slate-500 whitespace-nowrap">{fmtFecha(tipo === "colonial" ? s.iniciado_at : s.solicitada_at)}</td>
                      <td className="p-3 whitespace-nowrap">
                        {tipo === "colonial"
                          ? <Badge className="bg-emerald-100 text-emerald-800">La Colonial</Badge>
                          : <Badge className="bg-teal-100 text-teal-800">Seguros Crecer</Badge>}
                      </td>
                      <td className="p-3 font-medium dark:text-white">{tipo === "colonial" ? s.cliente : (s.nombre_envio || s.cliente)}</td>
                      <td className="p-3 font-mono whitespace-nowrap">{s.ultimos4 ? `•••• ${s.ultimos4}` : "—"}</td>
                      <td className="p-3">
                        {tipo === "colonial" ? `Póliza ${s.poliza} · ${s.monto}` : (
                          <span>Link de pago por {s.canal_envio === "ambos" ? "WhatsApp y correo" : s.canal_envio === "email" ? "correo" : "WhatsApp"}
                            <span className="text-slate-400 text-xs"> {[s.telefono_envio, s.email_envio].filter(Boolean).join(" · ")}</span>
                          </span>
                        )}
                      </td>
                      <td className="p-3">{tipo === "colonial" ? s.iniciado_por_nombre : s.solicitante_nombre}</td>
                      <td className="p-3 text-slate-500">{tipo === "colonial" ? (s.aprobado_por_nombre || "—") : "—"}</td>
                      <td className="p-3">
                        {tipo === "colonial"
                          ? <Badge className={ESTADO_PAGO[s.estado] || ""}>{(s.estado || "").replace(/_/g, " ")}</Badge>
                          : <Badge className={s.estado === "fallida" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}>{s.estado === "fallida" ? "fallido" : "link enviado"}</Badge>}
                      </td>
                      <td className="p-3">
                        {tipo === "colonial" && s.recibo ? (
                          <Button size="sm" variant="outline" className="h-8 px-2 text-emerald-700" title="Descargar recibo" onClick={() => descargarReciboSesion(s)} data-testid={`pago-hist-recibo-${s.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="metricas" className="mt-4">
            {metricas && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metricas-grid">
                {[
                  { l: "Tarjetas registradas", v: metricas.tarjetas.total, sub: `${metricas.tarjetas.internas} internas · ${metricas.tarjetas.por_cliente} por link` },
                  { l: "Solicitudes de vista", v: metricas.solicitudes.total, sub: `${metricas.solicitudes.pendientes} pendientes` },
                  { l: "Aprobadas", v: metricas.solicitudes.aprobadas, sub: `${metricas.solicitudes.rechazadas} rechazadas` },
                  { l: "Links a clientes", v: metricas.links.total, sub: `${metricas.links.completados} completados · ${metricas.links.pendientes} pendientes` },
                ].map((m) => (
                  <Card key={m.l}><CardContent className="p-4">
                    <p className="text-3xl font-bold dark:text-white">{m.v}</p>
                    <p className="text-sm font-medium mt-1">{m.l}</p>
                    <p className="text-xs text-slate-400">{m.sub}</p>
                  </CardContent></Card>
                ))}
                <Card className="col-span-2 md:col-span-4"><CardHeader className="pb-2"><CardTitle className="text-sm">Top aseguradoras solicitadas</CardTitle></CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    {metricas.top_aseguradoras.length === 0 ? <p className="text-sm text-slate-400">Sin datos aún</p> :
                      metricas.top_aseguradoras.map((a) => <Badge key={a.aseguradora} variant="outline">{a.aseguradora}: {a.total}</Badge>)}
                  </CardContent></Card>
                <Card className="col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm">Solicitudes de vista por usuario</CardTitle></CardHeader>
                  <CardContent className="p-0 overflow-x-auto max-h-[45vh] overflow-y-auto">
                    <table className="w-full text-sm" data-testid="metricas-por-usuario">
                      <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white">
                        <th className="p-2.5 font-semibold">Usuario</th><th className="p-2.5 font-semibold">Veces</th><th className="p-2.5 font-semibold whitespace-nowrap">Última solicitud</th>
                      </tr></thead>
                      <tbody>
                        {(metricas.solicitudes_por_usuario || []).length === 0 ? (
                          <tr><td colSpan={3} className="p-6 text-center text-slate-400">Sin solicitudes aún</td></tr>
                        ) : metricas.solicitudes_por_usuario.map((s, i) => (
                          <tr key={s.usuario} className={`border-b ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`}>
                            <td className="p-2.5 font-medium dark:text-white">{s.usuario}</td>
                            <td className="p-2.5">{s.total}</td>
                            <td className="p-2.5 text-slate-500 whitespace-nowrap">{fmtFecha(s.ultima)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent></Card>
                <Card className="col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm">Últimas solicitudes (fecha y hora)</CardTitle></CardHeader>
                  <CardContent className="p-0 overflow-x-auto max-h-[45vh] overflow-y-auto">
                    <table className="w-full text-sm" data-testid="metricas-ultimas">
                      <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white">
                        <th className="p-2.5 font-semibold whitespace-nowrap">Fecha/Hora</th><th className="p-2.5 font-semibold">Usuario</th><th className="p-2.5 font-semibold">Tarjeta</th><th className="p-2.5 font-semibold">Estado</th>
                      </tr></thead>
                      <tbody>
                        {(metricas.ultimas_solicitudes || []).length === 0 ? (
                          <tr><td colSpan={4} className="p-6 text-center text-slate-400">Sin solicitudes aún</td></tr>
                        ) : metricas.ultimas_solicitudes.map((s, i) => (
                          <tr key={i} className={`border-b ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`}>
                            <td className="p-2.5 text-slate-500 whitespace-nowrap">{fmtFecha(s.solicitada_at)}</td>
                            <td className="p-2.5 font-medium dark:text-white">{s.solicitante_nombre}</td>
                            <td className="p-2.5 font-mono whitespace-nowrap">•••• {s.ultimos4}</td>
                            <td className="p-2.5"><Badge className={ESTADO_BADGE[s.estado] || ""}>{s.estado}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent></Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ===== Dialog nueva tarjeta ===== */}
        <Dialog open={showNueva} onOpenChange={setShowNueva}>
          <DialogContent className="max-w-md" data-testid="nueva-tarjeta-dialog">
            <DialogHeader><DialogTitle>Nueva Tarjeta de Crédito</DialogTitle>
              <DialogDescription>Los datos se guardan cifrados. Solo se mostrarán los últimos 4 dígitos.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Cliente</Label><Input value={formT.cliente} onChange={(e) => setFormT({ ...formT, cliente: e.target.value })} data-testid="tarjeta-cliente" /></div>
              <div><Label>Titular de la tarjeta</Label><Input value={formT.titular} onChange={(e) => setFormT({ ...formT, titular: e.target.value })} data-testid="tarjeta-titular" /></div>
              <div><Label>Número de tarjeta</Label><Input inputMode="numeric" value={formT.numero} onChange={(e) => setFormT({ ...formT, numero: e.target.value })} data-testid="tarjeta-numero" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Vencimiento (MM/AA)</Label><Input placeholder="09/28" value={formT.vencimiento} onChange={(e) => setFormT({ ...formT, vencimiento: e.target.value })} data-testid="tarjeta-vencimiento" /></div>
                <div><Label>CVV</Label><Input inputMode="numeric" maxLength={4} value={formT.cvv} onChange={(e) => setFormT({ ...formT, cvv: e.target.value })} data-testid="tarjeta-cvv" /></div>
              </div>
              <div><Label>Notas (opcional)</Label><Textarea rows={2} value={formT.notas} onChange={(e) => setFormT({ ...formT, notas: e.target.value })} data-testid="tarjeta-notas" /></div>
              <div>
                <Label>Ejecutivo que podrá ver esta tarjeta (opcional)</Label>
                <Select value={formT.asignado_a || "none"} onValueChange={(v) => setFormT({ ...formT, asignado_a: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="tarjeta-asignado-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="none">Sin asignar (solo yo y mi jerarquía)</SelectItem>
                    {(asignables || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">El ejecutivo seleccionado y sus supervisores podrán ver esta tarjeta.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNueva(false)}>Cancelar</Button>
              <Button onClick={guardarTarjeta} disabled={guardando} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="guardar-tarjeta-btn">
                {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog solicitar vista / vista desbloqueada ===== */}
        <Dialog open={!!solTarjeta} onOpenChange={(v) => { if (!v) cerrarVista(); }}>
          <DialogContent className="max-w-md" data-testid="solicitar-vista-dialog">
            {datosVista ? (
              <>
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-emerald-600" />Datos de la tarjeta</DialogTitle>
                  <DialogDescription>{datosVista.expira_at ? "Vista temporal autorizada por su supervisor" : "Vista de administrador (registrada en el reporte)"}</DialogDescription></DialogHeader>
                <div className="p-4 rounded-xl bg-[#0C314C] text-white space-y-2" data-testid="tarjeta-desbloqueada">
                  <p className="text-xs opacity-70">{datosVista.marca} · {datosVista.cliente}</p>
                  <p className="text-xl font-mono tracking-wider" data-testid="tarjeta-numero-visible">{datosVista.numero.replace(/(\d{4})(?=\d)/g, "$1 ")}</p>
                  <div className="flex justify-between text-sm">
                    <span>{datosVista.titular}</span>
                    <span>Vence: {datosVista.vencimiento} · CVV: {datosVista.cvv}</span>
                  </div>
                </div>
                {datosVista.expira_at && (
                  <div className="flex items-center justify-center gap-2 text-amber-600 font-semibold" data-testid="vista-countdown">
                    <Timer className="w-5 h-5" />Se bloquea en {Math.floor(segRestantes / 60)}:{String(segRestantes % 60).padStart(2, "0")} min — puede cerrar y volver a abrir mientras dure
                  </div>
                )}
              </>
            ) : solActiva ? (
              <>
                <DialogHeader><DialogTitle>Esperando aprobación del supervisor</DialogTitle>
                  <DialogDescription>Se notificó a {solActiva.supervisor_nombre}. Esta pantalla se desbloqueará automáticamente al aprobar.</DialogDescription></DialogHeader>
                <div className="flex flex-col items-center py-6 gap-3" data-testid="esperando-aprobacion">
                  <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
                  <p className="text-sm text-slate-500">Solicitud enviada — {solActiva.aseguradora} · Póliza {solActiva.poliza}</p>
                </div>
                <DialogFooter><Button variant="outline" onClick={cerrarVista}>Cancelar</Button></DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader><DialogTitle>Solicitar ver datos de la tarjeta</DialogTitle>
                  <DialogDescription>Tarjeta •••• {solTarjeta?.ultimos4} de {solTarjeta?.cliente}. Indique qué va a pagar; su supervisor deberá aprobar.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Aseguradora a pagar</Label>
                    <Input placeholder="Buscar aseguradora..." value={filtroAseg} onChange={(e) => setFiltroAseg(e.target.value)} className="mb-2" data-testid="filtro-aseguradora" />
                    <div className="max-h-40 overflow-y-auto border rounded-md divide-y" data-testid="lista-aseguradoras">
                      {asegFiltradas.map((a) => (
                        <button key={a} type="button" onClick={() => setSolForm({ ...solForm, aseguradora: a })}
                          className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 ${solForm.aseguradora === a ? "bg-[#0C314C] text-white hover:bg-[#0C314C]" : ""}`}
                          data-testid={`aseguradora-opt-${a.replace(/\s+/g, "-")}`}>
                          {a}
                        </button>
                      ))}
                      {asegFiltradas.length === 0 && <p className="px-3 py-2 text-sm text-slate-400">Sin resultados</p>}
                    </div>
                  </div>
                  <div><Label>Póliza a pagar</Label><Input value={solForm.poliza} onChange={(e) => setSolForm({ ...solForm, poliza: e.target.value })} data-testid="solicitud-poliza" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={cerrarVista}>Cancelar</Button>
                  <Button onClick={solicitarVista} disabled={enviandoSol || !solForm.aseguradora || !solForm.poliza.trim()} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="enviar-solicitud-btn">
                    {enviandoSol && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Solicitar autorización
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ===== Dialog aprobar/rechazar (supervisor) ===== */}
        <Dialog open={!!aprobando} onOpenChange={(v) => { if (!v) { setAprobando(null); setPassSup(""); setMotivoRechazo(""); } }}>
          <DialogContent className="max-w-md" data-testid="aprobar-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" />Autorizar vista de tarjeta</DialogTitle>
              <DialogDescription>
                {aprobando?.solicitante_nombre} quiere ver la tarjeta •••• {aprobando?.ultimos4} de <strong>{aprobando?.cliente}</strong> para pagar la póliza <strong>{aprobando?.poliza}</strong> de <strong>{aprobando?.aseguradora}</strong>.
              </DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Confirme con su contraseña de la plataforma</Label>
                <Input type="password" value={passSup} onChange={(e) => setPassSup(e.target.value)} data-testid="password-supervisor" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Motivo (solo si va a rechazar)</Label>
                <Input value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)} data-testid="motivo-rechazo" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="text-red-600 border-red-200" onClick={rechazar} disabled={procesando} data-testid="rechazar-btn">
                <XCircle className="w-4 h-4 mr-1" />Rechazar
              </Button>
              <Button onClick={aprobar} disabled={procesando || !passSup} className="bg-emerald-600 hover:bg-emerald-700" data-testid="aprobar-btn">
                {procesando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Aprobar (1 min)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog generar link ===== */}
        <Dialog open={showLink} onOpenChange={setShowLink}>
          <DialogContent className="max-w-md" data-testid="generar-link-dialog">
            <DialogHeader><DialogTitle>Generar link para el cliente</DialogTitle>
              <DialogDescription>El cliente completará los datos de su tarjeta de forma segura. El link se invalida al guardar y usted recibirá una alerta.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre del cliente</Label><Input value={linkForm.cliente} onChange={(e) => setLinkForm({ ...linkForm, cliente: e.target.value })} data-testid="link-cliente" /></div>
              <div><Label>Nota (opcional)</Label><Input value={linkForm.nota} onChange={(e) => setLinkForm({ ...linkForm, nota: e.target.value })} data-testid="link-nota" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLink(false)}>Cancelar</Button>
              <Button onClick={crearLink} disabled={creandoLink || !linkForm.cliente.trim()} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="crear-link-btn">
                {creandoLink && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Generar y copiar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* ===== Dialog enviar link por correo ===== */}
        <Dialog open={!!emailLink} onOpenChange={(v) => { if (!v) { setEmailLink(null); setEmailDest(""); } }}>
          <DialogContent className="max-w-md" data-testid="enviar-email-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#0C314C]" />Enviar link por correo</DialogTitle>
              <DialogDescription>Se enviará al cliente <strong>{emailLink?.cliente}</strong> el link de registro seguro usando la plantilla "Cobros - Link de Registro de Tarjeta" (configurable en Plantillas de Correos).</DialogDescription></DialogHeader>
            <div>
              <Label>Correo del cliente</Label>
              <Input type="email" placeholder="cliente@correo.com" value={emailDest} onChange={(e) => setEmailDest(e.target.value)} data-testid="email-destinatario" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEmailLink(null); setEmailDest(""); }}>Cancelar</Button>
              <Button onClick={enviarEmail} disabled={enviandoEmail || !emailDest.trim()} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="enviar-email-btn">
                {enviandoEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Enviar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog ver notas ===== */}
        <Dialog open={!!verNotas} onOpenChange={(v) => { if (!v) setVerNotas(null); }}>
          <DialogContent className="max-w-md" data-testid="ver-notas-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><StickyNote className="w-5 h-5 text-[#0C314C]" />Notas de la tarjeta</DialogTitle>
              <DialogDescription>Tarjeta •••• {verNotas?.ultimos4} de {verNotas?.cliente}</DialogDescription></DialogHeader>
            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border text-sm min-h-[60px]" data-testid="notas-contenido">
              {verNotas?.notas?.trim() ? verNotas.notas : <span className="text-slate-400">Esta tarjeta no tiene notas registradas.</span>}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setVerNotas(null)}>Cerrar</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog historial / trazabilidad ===== */}
        <Dialog open={!!historial} onOpenChange={(v) => { if (!v) setHistorial(null); }}>
          <DialogContent className="max-w-lg" data-testid="historial-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-[#0C314C]" />Trazabilidad de la tarjeta</DialogTitle>
              <DialogDescription>•••• {historial?.tarjeta?.ultimos4} de {historial?.tarjeta?.cliente} — todo lo ocurrido desde su creación</DialogDescription></DialogHeader>
            <div className="max-h-[55vh] overflow-y-auto space-y-0 pr-1" data-testid="historial-timeline">
              <div className="relative pl-6 pb-4 border-l-2 border-slate-200 dark:border-slate-700 ml-2">
                <span className="absolute -left-[7px] top-0 w-3 h-3 rounded-full bg-[#0C314C]" />
                <p className="text-sm font-medium dark:text-white">Tarjeta creada {historial?.tarjeta?.origen === "cliente" ? "por el cliente (vía link)" : "internamente"}</p>
                <p className="text-xs text-slate-500">{fmtFecha(historial?.tarjeta?.created_at)} · {historial?.tarjeta?.created_by_name}</p>
              </div>
              {(historial?.eventos || []).map((e, i) => (
                <div key={i} className="relative pl-6 pb-4 border-l-2 border-slate-200 dark:border-slate-700 ml-2">
                  <span className={`absolute -left-[7px] top-0 w-3 h-3 rounded-full ${e.estado === "rechazada" || e.estado === "eliminada" ? "bg-red-500" : e.estado === "vista_admin" ? "bg-violet-500" : e.estado === "pendiente" ? "bg-amber-500" : "bg-emerald-500"}`} />
                  <p className="text-sm font-medium dark:text-white">
                    {e.accion === "vista_admin" ? `Vista directa de administrador por ${e.solicitante_nombre}`
                      : e.accion === "eliminacion" ? `Tarjeta eliminada por ${e.solicitante_nombre}`
                      : e.accion === "edicion" ? `Tarjeta editada por ${e.solicitante_nombre} (${(e.campos_editados || []).join(", ")})`
                      : e.accion === "pago_colonial" ? `Pago en La Colonial por ${e.solicitante_nombre} — Póliza ${e.poliza} · Monto ${e.monto || "-"}`
                      : e.accion === "envio_aseguradora" ? `${e.solicitante_nombre} envió la tarjeta a la aseguradora (${(e.aseguradora || "").replace("Enviada a ", "")})`
                      : e.accion === "envio_link_crecer" ? `${e.solicitante_nombre} envió el link de pago de Seguros Crecer al cliente ${e.nombre_envio || ""} (${e.canal_envio === "ambos" ? "WhatsApp y correo" : e.canal_envio === "email" ? "correo" : "WhatsApp"})`
                      : `Solicitud de vista de ${e.solicitante_nombre} — ${e.aseguradora} · Póliza ${e.poliza}`}
                    {" "}<Badge className={`${ESTADO_BADGE[e.estado] || ""} ml-1 text-xs`}>{e.estado}</Badge>
                  </p>
                  <p className="text-xs text-slate-500">{e.accion === "envio_aseguradora" ? "Enviada" : "Solicitada"}: {fmtFecha(e.solicitada_at)}</p>
                  {e.accion === "envio_aseguradora" && e.visto_at && <p className="text-xs text-cyan-700">Vista por la aseguradora (con OTP): {fmtFecha(e.visto_at)}</p>}
                  {e.aprobada_at && <p className="text-xs text-emerald-600">Aprobada: {fmtFecha(e.aprobada_at)} · {e.aprobada_por_nombre || e.supervisor_nombre}</p>}
                  {e.rechazada_at && <p className="text-xs text-red-600">Rechazada: {fmtFecha(e.rechazada_at)}{e.motivo_rechazo ? ` · ${e.motivo_rechazo}` : ""}</p>}
                  {e.visto_at && <p className="text-xs text-slate-500">Vista por el usuario: {fmtFecha(e.visto_at)}</p>}
                  {e.motivo_eliminacion && <p className="text-xs text-red-600">Motivo: {e.motivo_eliminacion}</p>}
                </div>
              ))}
              {(historial?.eventos || []).length === 0 && (
                <p className="text-sm text-slate-400 pl-6">No se han registrado solicitudes de vista para esta tarjeta.</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistorial(null)}>Cerrar</Button>
              <Button onClick={exportarHistorialPdf} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="exportar-historial-pdf-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1" />Exportar PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog enviar a aseguradora ===== */}
        <Dialog open={!!compartirT} onOpenChange={(v) => { if (!v) { setCompartirT(null); setEmailAseg(""); } }}>
          <DialogContent className="max-w-md" data-testid="compartir-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#0C314C]" />Enviar tarjeta a la aseguradora</DialogTitle>
              <DialogDescription>
                Se enviará un correo con acceso protegido a la tarjeta •••• {compartirT?.ultimos4} de <strong>{compartirT?.cliente}</strong>. La aseguradora deberá solicitar un código OTP que le llegará a <strong>usted</strong> — solo podrá ver los datos cuando usted le suministre el código. Todo queda en la trazabilidad.
              </DialogDescription></DialogHeader>
            <div>
              <Label>Correo del ejecutivo de la aseguradora</Label>
              <Input type="email" placeholder="ejecutivo@aseguradora.com.do" value={emailAseg} onChange={(e) => setEmailAseg(e.target.value)} data-testid="email-aseguradora" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCompartirT(null); setEmailAseg(""); }}>Cancelar</Button>
              <Button onClick={compartirAseguradora} disabled={compartiendo || !emailAseg.trim()} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="compartir-enviar-btn">
                {compartiendo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Enviar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog editar tarjeta (admin/supervisor) ===== */}
        <Dialog open={!!editarT} onOpenChange={(v) => { if (!v) setEditarT(null); }}>
          <DialogContent className="max-w-md" data-testid="editar-tarjeta-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-indigo-600" />Editar tarjeta •••• {editarT?.ultimos4}</DialogTitle>
              <DialogDescription>Deje número, vencimiento o CVV en blanco para no cambiarlos. Los cambios quedan en la trazabilidad.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Cliente</Label><Input value={formE.cliente} onChange={(e) => setFormE({ ...formE, cliente: e.target.value })} data-testid="edit-cliente" /></div>
              <div><Label>Titular</Label><Input value={formE.titular} onChange={(e) => setFormE({ ...formE, titular: e.target.value })} data-testid="edit-titular" /></div>
              <div><Label>Número de tarjeta</Label><Input inputMode="numeric" placeholder={`•••• •••• •••• ${editarT?.ultimos4} (sin cambio)`} value={formE.numero} onChange={(e) => setFormE({ ...formE, numero: e.target.value })} data-testid="edit-numero" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Vencimiento (MM/AA)</Label><Input placeholder="Sin cambio" value={formE.vencimiento} onChange={(e) => setFormE({ ...formE, vencimiento: e.target.value })} data-testid="edit-vencimiento" /></div>
                <div><Label>CVV</Label><Input inputMode="numeric" maxLength={4} placeholder="Sin cambio" value={formE.cvv} onChange={(e) => setFormE({ ...formE, cvv: e.target.value })} data-testid="edit-cvv" /></div>
              </div>
              <div><Label>Notas</Label><Textarea rows={2} value={formE.notas} onChange={(e) => setFormE({ ...formE, notas: e.target.value })} data-testid="edit-notas" /></div>
              <div className="pt-1 border-t">
                <Label>Ejecutivos con acceso a esta tarjeta</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="edit-asignados-lista">
                  {(editarT?.asignados || []).length === 0 && <span className="text-xs text-slate-400">Sin asignaciones — la ven el creador y su jerarquía.</span>}
                  {(editarT?.asignados || []).map((a) => (
                    <Badge key={a.id} variant="outline" className="gap-1 pr-1 font-normal" title={`Asignado por ${a.asignado_por_nombre || "—"}`}>
                      {a.nombre}
                      <button onClick={() => quitarAsignado(a.id)} className="ml-0.5 rounded-full hover:bg-red-100 text-red-500 px-1" data-testid={`quitar-asignado-${a.id}`}>×</button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Select value={nuevoAsignado || "none"} onValueChange={(v) => setNuevoAsignado(v === "none" ? "" : v)}>
                    <SelectTrigger className="flex-1" data-testid="edit-asignado-select"><SelectValue placeholder="Agregar ejecutivo..." /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="none">Seleccionar ejecutivo...</SelectItem>
                      {(asignables || []).filter((u) => !(editarT?.asignados || []).some((a) => a.id === u.id)).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={agregarAsignado} disabled={!nuevoAsignado || asignandoEj} data-testid="agregar-asignado-btn">
                    {asignandoEj ? <Loader2 className="w-4 h-4 animate-spin" /> : "Agregar"}
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Cada ejecutivo asignado (y sus supervisores) podrá ver la tarjeta. Los cambios quedan en la trazabilidad.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditarT(null)}>Cancelar</Button>
              <Button onClick={guardarEdicion} disabled={editando} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="guardar-edicion-btn">
                {editando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog importar tarjetas (admin) ===== */}
        <Dialog open={showImport} onOpenChange={(v) => { if (!v) setShowImport(false); }}>
          <DialogContent className="max-w-lg" data-testid="importar-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-[#0C314C]" />Importar tarjetas masivamente</DialogTitle>
              <DialogDescription>
                Suba el Excel basado en la plantilla. La columna <strong>Usuario</strong> define a quién queda enlazada cada tarjeta (visible para ese usuario y sus supervisores según la jerarquía). Los datos se cifran al importar.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Button variant="outline" size="sm" onClick={descargarPlantilla} data-testid="descargar-plantilla-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1" />Descargar plantilla
              </Button>
              <div>
                <Label>Archivo Excel (.xlsx)</Label>
                <Input type="file" accept=".xlsx" onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }} data-testid="import-file" />
              </div>
              {importResult && (
                <div className="rounded-lg border p-3 space-y-2 text-sm" data-testid="import-result">
                  <p className="font-medium text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />{importResult.creadas} tarjeta(s) importada(s)</p>
                  {(importResult.errores || []).length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      <p className="font-medium text-red-600">{importResult.errores.length} fila(s) con error:</p>
                      {importResult.errores.map((er, i) => (
                        <p key={i} className="text-xs text-red-600">Fila {er.fila}: {er.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowImport(false)}>Cerrar</Button>
              <Button onClick={importarTarjetas} disabled={importando || !importFile} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="importar-confirmar-btn">
                {importando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Importar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog eliminar link (admin) ===== */}
        <Dialog open={!!eliminarLink} onOpenChange={(v) => { if (!v) setEliminarLink(null); }}>
          <DialogContent className="max-w-sm" data-testid="eliminar-link-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" />Eliminar link</DialogTitle>
              <DialogDescription>
                ¿Eliminar el link de registro de <strong>{eliminarLink?.cliente}</strong>? {eliminarLink?.estado === "pendiente" ? "El cliente ya no podrá usarlo." : ""} Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEliminarLink(null)}>Cancelar</Button>
              <Button onClick={confirmarEliminarLink} disabled={eliminandoLink} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirmar-eliminar-link-btn">
                {eliminandoLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog Pagar en La Colonial ===== */}
        <Dialog open={!!pagoT} onOpenChange={(v) => { if (!v) cerrarPago(); }}>
          <DialogContent className="max-w-lg" data-testid="pago-colonial-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-emerald-600" />Pagar en La Colonial — •••• {pagoT?.ultimos4}</DialogTitle>
              <DialogDescription>
                El robot conversa con el bot de WhatsApp de La Colonial (809-508-8000) usando esta tarjeta. Antes de confirmar el pago pedirá aprobación de un supervisor o administrador. Todo queda en la trazabilidad.
              </DialogDescription>
            </DialogHeader>
            {!pagoSesion ? (
              <div className="space-y-3">
                <div><Label>Cédula / RNC del cliente (sin guiones)</Label>
                  <Input inputMode="numeric" value={pagoForm.cedula} onChange={(e) => setPagoForm({ ...pagoForm, cedula: e.target.value })} data-testid="pago-cedula" /></div>
                <div><Label>Número de póliza</Label>
                  <Input value={pagoForm.poliza} onChange={(e) => setPagoForm({ ...pagoForm, poliza: e.target.value })} data-testid="pago-poliza" /></div>
                <div><Label>Monto a pagar</Label>
                  <Input inputMode="decimal" placeholder="Ej: 300" value={pagoForm.monto} onChange={(e) => setPagoForm({ ...pagoForm, monto: e.target.value })} data-testid="pago-monto" /></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge className={`${ESTADO_PAGO[pagoSesion.estado] || ""}`} data-testid="pago-estado-badge">{(pagoSesion.estado || "").replace(/_/g, " ")}</Badge>
                  <span className="text-xs text-slate-400">Póliza {pagoSesion.poliza} · Monto {pagoSesion.monto}</span>
                </div>
                <div className="max-h-56 overflow-y-auto space-y-1.5 rounded-lg border p-2 bg-slate-50 dark:bg-slate-800/40" data-testid="pago-eventos">
                  {(pagoSesion.eventos || []).length === 0 && (
                    <p className="text-xs text-slate-400 p-2">Iniciando conversación con el bot de La Colonial...</p>
                  )}
                  {(pagoSesion.eventos || []).map((ev, i) => (
                    <div key={i} className={`text-xs rounded-md px-2 py-1.5 whitespace-pre-wrap ${ev.quien === "bot" ? "bg-white dark:bg-slate-800 border" : ev.quien === "sistema" ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-900 border border-emerald-200 ml-6"}`}>
                      <span className="font-semibold mr-1">{ev.quien === "bot" ? "La Colonial:" : ev.quien === "sistema" ? "Sistema:" : "Robot:"}</span>{ev.texto}
                    </div>
                  ))}
                </div>
                {pagoSesion.estado === "esperando_aprobacion" && ((alcance.es_admin || alcance.es_supervisor) ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" />El bot pide confirmar el pago. Revise el resumen arriba y apruebe con su contraseña.</p>
                    <Input type="password" placeholder="Su contraseña de la plataforma" value={passPago} onChange={(e) => setPassPago(e.target.value)} data-testid="pago-pass" />
                    <Button onClick={aprobarPago} disabled={aprobandoPago || !passPago} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pago-aprobar-btn">
                      {aprobandoPago ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Aprobar y confirmar pago
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">Esperando aprobación de su supervisor o un administrador para confirmar el pago...</p>
                ))}
                {pagoSesion.estado === "completado" && (
                  <div className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-2">
                    <p className="text-sm font-medium text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Pago completado por el bot de La Colonial.</p>
                    {pagoSesion.recibo ? (
                      <div className="space-y-2">
                        <Button size="sm" variant="outline" onClick={descargarRecibo} data-testid="pago-descargar-recibo">
                          <Download className="w-4 h-4 mr-1" />Descargar recibo
                        </Button>
                        <div className="flex gap-1.5">
                          <Input type="email" placeholder="Enviar recibo a: correo@ejemplo.com" value={emailRecibo} onChange={(e) => setEmailRecibo(e.target.value)} data-testid="pago-email-recibo" />
                          <Button size="sm" onClick={enviarRecibo} disabled={enviandoRecibo || !emailRecibo.trim()} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="pago-enviar-recibo-btn">
                            {enviandoRecibo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-emerald-700">Cuando el bot envíe el recibo, aparecerá aquí para descargarlo o enviarlo por correo.</p>
                    )}
                    <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setPagoSesion(null)} data-testid="pago-nuevo-btn">Hacer otro pago con esta tarjeta</Button>
                  </div>
                )}
                {["error", "cancelado", "expirado"].includes(pagoSesion.estado) && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
                    <p className="text-sm text-red-700 flex items-center gap-1.5"><XCircle className="w-4 h-4" />{pagoSesion.error || `Pago ${pagoSesion.estado}`}</p>
                    <Button size="sm" variant="outline" onClick={() => setPagoSesion(null)} data-testid="pago-reintentar-btn">Intentar de nuevo</Button>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              {!pagoSesion ? (
                <>
                  <Button variant="outline" onClick={cerrarPago}>Cancelar</Button>
                  <Button onClick={iniciarPago} disabled={iniciandoPago || !pagoForm.cedula.trim() || !pagoForm.poliza.trim() || !pagoForm.monto.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pago-iniciar-btn">
                    {iniciandoPago ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}Iniciar pago con el robot
                  </Button>
                </>
              ) : (
                <>
                  {ESTADOS_PAGO_ACTIVOS.includes(pagoSesion.estado) && (
                    <Button variant="outline" className="text-red-600 border-red-200" onClick={cancelarPago} data-testid="pago-cancelar-btn">Cancelar pago</Button>
                  )}
                  <Button variant="outline" onClick={cerrarPago}>Cerrar</Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog Enviar Pago Seguros Crecer ===== */}
        <Dialog open={showCrecer} onOpenChange={(v) => { if (!v) setShowCrecer(false); }}>
          <DialogContent className="max-w-md" data-testid="crecer-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-teal-600" />Enviar Pago Seguros Crecer{crecerT ? ` — ${crecerT.cliente}` : ""}</DialogTitle>
              <DialogDescription>
                Se envía al cliente el link oficial de pago en línea de Seguros Crecer. El cliente ingresa su Cédula/Pasaporte/RNC y apellido, selecciona las pólizas a pagar y paga con su tarjeta directamente en el portal.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nombre del cliente *</Label>
                <Input value={crecerForm.nombre} onChange={(e) => setCrecerForm({ ...crecerForm, nombre: e.target.value })} placeholder="Juan Pérez" data-testid="crecer-nombre" />
              </div>
              <div>
                <Label>Canal de envío</Label>
                <div className="flex gap-2 mt-1">
                  {[["whatsapp", "WhatsApp"], ["email", "Correo"], ["ambos", "Ambos"]].map(([v, l]) => (
                    <Button key={v} type="button" size="sm" variant={crecerForm.canal === v ? "default" : "outline"}
                      className={crecerForm.canal === v ? "bg-teal-600 hover:bg-teal-700 text-white" : ""}
                      onClick={() => setCrecerForm({ ...crecerForm, canal: v })} data-testid={`crecer-canal-${v}`}>{l}</Button>
                  ))}
                </div>
              </div>
              {crecerForm.canal !== "email" && (
                <div>
                  <Label>Teléfono WhatsApp del cliente *</Label>
                  <Input value={crecerForm.telefono} onChange={(e) => setCrecerForm({ ...crecerForm, telefono: e.target.value })} placeholder="8095551234" data-testid="crecer-telefono" />
                </div>
              )}
              {crecerForm.canal !== "whatsapp" && (
                <div>
                  <Label>Correo electrónico del cliente *</Label>
                  <Input type="email" value={crecerForm.email} onChange={(e) => setCrecerForm({ ...crecerForm, email: e.target.value })} placeholder="cliente@correo.com" data-testid="crecer-email" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCrecer(false)}>Cancelar</Button>
              <Button onClick={enviarCrecer}
                disabled={enviandoCrecer || !crecerForm.nombre.trim() || (crecerForm.canal !== "email" && !crecerForm.telefono.trim()) || (crecerForm.canal !== "whatsapp" && !crecerForm.email.trim())}
                className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="crecer-enviar-btn">
                {enviandoCrecer ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Banknote className="w-4 h-4 mr-1" />}Enviar Link
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog eliminar tarjeta (admin) ===== */}
        <Dialog open={!!eliminarT} onOpenChange={(v) => { if (!v) { setEliminarT(null); setMotivoElim(""); } }}>
          <DialogContent className="max-w-md" data-testid="eliminar-tarjeta-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" />Eliminar tarjeta</DialogTitle>
              <DialogDescription>
                ¿Seguro que desea eliminar la tarjeta •••• {eliminarT?.ultimos4} de <strong>{eliminarT?.cliente}</strong>? Esta acción es permanente y quedará registrada en el reporte con su usuario, fecha y hora.
              </DialogDescription></DialogHeader>
            <div>
              <Label>Motivo (opcional)</Label>
              <Input value={motivoElim} onChange={(e) => setMotivoElim(e.target.value)} data-testid="motivo-eliminacion" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEliminarT(null); setMotivoElim(""); }}>Cancelar</Button>
              <Button onClick={eliminarTarjeta} disabled={eliminando} className="bg-red-600 hover:bg-red-700" data-testid="confirmar-eliminar-btn">
                {eliminando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Eliminar definitivamente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
